import Button from "@common/components/Button";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import RightDrawer from "@common/components/RightDrawer";
import { useSimpleReducer } from "@common/hooks";
import { CircularProgress, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Room } from "@services/rooms/types";
import { Location } from "@services/locations/types";
import React, { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import RoomSelection from "./RoomSelection";
import SelectedRooms from "./SelectedRooms";
import {
  addParticipants,
  getAccountLocations,
  getUserFavoritesRooms,
  getUserRecentsRooms,
} from "@services/meetings";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  ConsultedRooms,
  CustomRoom,
  MeetingInstance,
} from "@services/meetings/types";

type AddParticipantDrawerProps = {
  meeting?: MeetingInstance;
  open: boolean;
  setOpen: Function;
};
type AddParticipantReducer = {
  selectedRooms: Array<Room | CustomRoom>;
  loadingData: boolean;
  loadingInvite: boolean;
  directory: Location[];
  favorites: ConsultedRooms;
  recents: ConsultedRooms;
};
export const AddParticipantContext = createContext<
  AddParticipantReducer & {
    dispatch: React.Dispatch<"reset" | Partial<AddParticipantReducer>>;
    fetchData: Function;
    isCustomRoom: (room: Room | CustomRoom) => room is CustomRoom;
  }
>({
  selectedRooms: [],
  loadingData: true,
  loadingInvite: false,
  directory: [],
  favorites: { external: [], internal: [] },
  recents: { external: [], internal: [] },
  dispatch: () => {},
  fetchData: () => {},
  isCustomRoom: (room): room is CustomRoom => true,
});

const AddParticipantDrawer = (props: AddParticipantDrawerProps) => {
  const { meeting, open, setOpen } = props;
  const [user] = useAuthenticatedUser();
  const context = React.useContext(RoomsRealtimeContext);
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [
    {
      selectedRooms,
      loadingData,
      loadingInvite,
      directory,
      favorites,
      recents,
    },
    dispatch,
  ] = useSimpleReducer<AddParticipantReducer>({
    selectedRooms: [],
    loadingData: true,
    loadingInvite: false,
    directory: [],
    favorites: { external: [], internal: [] },
    recents: { external: [], internal: [] },
  });

  const fetchData = async () => {
    try {
      Promise.all([
        getAccountLocations(meeting?.account?.id),
        getUserFavoritesRooms(user?.id),
        getUserRecentsRooms(user?.id),
      ]).then(([directory, favorites, recents]) => {
        let tmp = selectedRooms;
        favorites.external.forEach(({ name, address, id }) =>
          tmp.forEach((selected, index) => {
            if (
              isCustomRoom(selected) &&
              selected.name === name &&
              selected.address === address
            )
              tmp[index].id = id;
          })
        );
        dispatch({
          directory,
          favorites,
          recents,
          loadingData: false,
          selectedRooms: tmp,
        });
      });
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    if (meeting && loadingData) fetchData();
  }, [meeting]);

  const isCustomRoom = (room: Room | CustomRoom): room is CustomRoom =>
    (room as CustomRoom).address !== undefined;

  const onClose = () => {
    dispatch({ selectedRooms: [] });
    setOpen();
  };

  const clickInvite = async () => {
    try {
      const toAdd = selectedRooms.map((room) =>
        isCustomRoom(room)
          ? {
              address: room.address,
              extension: room.extension ?? "",
              name: room.name,
              protocol: room.protocol,
            }
          : { room: { id: room.id } }
      );
      await addParticipants(toAdd, meeting?.id?.toString());
      dispatch({ selectedRooms: [] });
      setOpen();
    } catch (e) {
      dispatchError(e);
    }
  };

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={t("Add Participant")}
      width="1000px"
      closeButton={
        <Grid
          sx={(theme) => ({
            "&>.MuiButton-root": { marginRight: theme.spacing(1) },
          })}>
          <Button
            color="white"
            onClick={() => clickInvite()}
            loading={loadingInvite}
            disabled={selectedRooms.length === 0}
          >
            Invite
          </Button>
          <Button
            color="greyed"
            variant="outlined"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </Grid>
      }
    >
      <AddParticipantContext.Provider
        value={{
          selectedRooms,
          loadingData,
          loadingInvite,
          directory,
          favorites,
          recents,
          dispatch,
          fetchData,
          isCustomRoom,
        }}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{ width: '100%', height: "100%",
            gap: "1rem",
            paddingX: "1rem", }}
        >
          <Grid
            sx={{
              width: "50%",
            }}>
            {loadingData ? <CircularProgress /> : <RoomSelection />}
          </Grid>
          <Divider orientation="vertical" />
          <Grid
            sx={{
              width: "50%",
            }}>
            <SelectedRooms />
          </Grid>
        </Grid>
      </AddParticipantContext.Provider>
    </RightDrawer>
  );
};

export default AddParticipantDrawer;
